import React from 'react';
import { Sparkles, Zap, Target, Users, Rocket, Heart } from 'lucide-react';

const AboutPage = () => {
  const features = [
    {
      icon: Sparkles,
      title: 'Inteligência Artificial Avançada',
      description: 'Tecnologia que entende o contexto do seu negócio, perfil do público e tom de comunicação.'
    },
    {
      icon: Zap,
      title: 'Automação Completa',
      description: 'Da ideia ao agendamento, tudo em um único ambiente integrado e eficiente.'
    },
    {
      icon: Target,
      title: 'Resultados Estratégicos',
      description: 'Conteúdo personalizado e orientado a objetivos concretos de crescimento digital.'
    },
    {
      icon: Users,
      title: 'Para Todos os Perfis',
      description: 'De marcas pessoais a grandes empresas, nossa plataforma se adapta às suas necessidades.'
    }
  ];

  const values = [
    {
      icon: Heart,
      title: 'Criatividade em Primeiro Lugar',
      description: 'A IA é sua aliada estratégica, não sua substituta. Amplifique seu potencial criativo.'
    },
    {
      icon: Rocket,
      title: 'Simplicidade e Eficiência',
      description: 'Eliminamos processos complexos para você focar no que realmente importa: crescer.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-amber-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Background Decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-br from-amber-200/30 to-orange-200/30 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-br from-amber-200/20 to-orange-200/20 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 py-16 lg:py-24">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl mb-6 shadow-lg">
              <Sparkles className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Sobre Nós
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Transformando a forma como marcas criam conteúdo digital
            </p>
          </div>

          {/* Missão Principal */}
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-8 lg:p-12 border border-gray-100 mb-12">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Target className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Nossa Missão
                  </h2>
                  <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
                    <p>
                      Criar conteúdo relevante e consistente é um dos maiores desafios para marcas e profissionais hoje. Foi a partir dessa necessidade que criamos nossa plataforma.
                    </p>
                    <p>
                      Somos uma <span className="font-semibold text-amber-700">solução completa de automação de conteúdo com inteligência artificial</span>, desenvolvida para simplificar, acelerar e profissionalizar a presença digital de empresas, criadores e marcas pessoais.
                    </p>
                    <p>
                      Nossa tecnologia entende o contexto do seu negócio, o perfil do seu público e o tom da sua comunicação para gerar posts personalizados, legendas estratégicas e publicações prontas para ir ao ar.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Diferenciais */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-10">
              Por Que Somos Diferentes
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={index}
                    className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all hover:scale-[1.02]"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                          {feature.title}
                        </h3>
                        <p className="text-gray-600 leading-relaxed">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Como Funciona */}
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl shadow-xl p-8 lg:p-12 border border-amber-100 mb-12">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">
              Como Funciona
            </h2>
            <div className="max-w-4xl mx-auto space-y-6 text-lg text-gray-700 leading-relaxed">
              <p>
                Centralizamos todo o fluxo de criação em um único ambiente: <span className="font-semibold text-amber-800">da ideia ao texto, da personalização ao agendamento</span>. Assim, eliminamos processos complexos, reduzimos custos e devolvemos autonomia a quem quer crescer no digital com consistência e eficiência.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-white rounded-2xl shadow-md flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl font-bold text-amber-600">1</span>
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">Conte sua ideia</h3>
                  <p className="text-sm text-gray-600">A IA entende seu contexto e objetivos</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-white rounded-2xl shadow-md flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl font-bold text-amber-600">2</span>
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">Personalize</h3>
                  <p className="text-sm text-gray-600">Ajuste textos, visuais e tom de voz</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-white rounded-2xl shadow-md flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl font-bold text-amber-600">3</span>
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">Publique</h3>
                  <p className="text-sm text-gray-600">Agende ou publique instantaneamente</p>
                </div>
              </div>
            </div>
          </div>

          {/* Valores */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-10">
              Nossos Valores
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {values.map((value, index) => {
                const Icon = value.icon;
                return (
                  <div
                    key={index}
                    className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100"
                  >
                    <div className="flex flex-col items-center text-center">
                      <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center mb-4 shadow-md">
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">
                        {value.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {value.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Nossa Filosofia */}
          <div className="bg-gradient-to-r from-amber-400 to-orange-500 rounded-3xl shadow-2xl p-8 lg:p-12 text-white text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Nossa Filosofia
            </h2>
            <p className="text-xl lg:text-2xl leading-relaxed max-w-4xl mx-auto mb-6">
              Acreditamos que tecnologia deve trabalhar <span className="font-bold underline decoration-white/50">a favor da criatividade</span> — não substituí-la.
            </p>
            <p className="text-lg opacity-90 max-w-3xl mx-auto">
              Por isso, nossa IA atua como uma aliada estratégica, permitindo que você produza mais, com qualidade, em menos tempo.
            </p>
          </div>

          {/* Visão de Futuro */}
          <div className="mt-12 text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl mb-6 shadow-lg">
              <Rocket className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              O Futuro da Criação de Conteúdo
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed">
              Estamos construindo uma nova forma de criar conteúdo: <span className="font-bold text-amber-700">mais inteligente, acessível e orientada a resultados</span>.
            </p>
          </div>

          {/* CTA Final */}
          <div className="mt-16 text-center">
            <a
              href="/contents/new"
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-white text-lg font-bold rounded-2xl shadow-lg hover:shadow-xl transition-all hover:scale-[1.02] active:scale-[0.98]"
            >
              <Sparkles className="w-6 h-6" />
              Comece a Criar Agora
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;